/**
 * Unified Delivery Calculation Service
 * Implements the exact business logic from DeliveryMethodsDocumentationUpdated.md
 * Handles Express, Rápido, and Estándar delivery method calculations
 */

import { DateService } from "../utils/dateUtils";

// Add interface for object format (actual partner.daysOpen structure)
export interface PartnerScheduleObject {
  monday: boolean;
  mondayOpeningTime: string;
  mondayClosingTime: string;
  tuesday: boolean;
  tuesdayOpeningTime: string;
  tuesdayClosingTime: string;
  wednesday: boolean;
  wednesdayOpeningTime: string;
  wednesdayClosingTime: string;
  thursday: boolean;
  thursdayOpeningTime: string;
  thursdayClosingTime: string;
  friday: boolean;
  fridayOpeningTime: string;
  fridayClosingTime: string;
  saturday: boolean;
  saturdayOpeningTime: string;
  saturdayClosingTime: string;
  sunday: boolean;
  sundayOpeningTime: string;
  sundayClosingTime: string;
}

// Array format interface
export interface PartnerScheduleArray {
  dayOfWeek: number; // 0=Sunday, 1=Monday, etc.
  openTime: string; // '09:00'
  closeTime: string; // '18:00'
  isOpen: boolean;
}

export interface DeliveryInput {
  logistics: {
    expressDelivery: boolean;
    limitTime: string | boolean; // '14:00' format or boolean
    daysToDeliver: number;
    minDaysToDeliver?: number;
    weight?: number;
  };
  partnerSchedule: PartnerScheduleObject | PartnerScheduleArray[]; // Accept both formats
  zones: {
    originFeeZoneId: string;
    originLeadTimeZoneId: string;
    destinationFeeZoneId: string;
    destinationLeadTimeZoneId: string;
  };
  opsLeadTime: number; // Already extracted from opsLeadTime.opsLeadTime
}

// Internal normalized input interface (after transformation)
interface NormalizedDeliveryInput {
  logistics: {
    expressDelivery: boolean;
    limitTime: string | boolean;
    daysToDeliver: number;
    minDaysToDeliver?: number;
    weight?: number;
  };
  partnerSchedule: PartnerScheduleArray[]; // Always array format internally
  zones: {
    originFeeZoneId: string;
    originLeadTimeZoneId: string;
    destinationFeeZoneId: string;
    destinationLeadTimeZoneId: string;
  };
  opsLeadTime: number;
}

export interface DeliveryMethodAvailability {
  express: boolean;
  rapido: boolean;
  standard: boolean;
}

export interface DeliveryOptions {
  express: {
    available: boolean;
    deliveryDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
    nextAvailableTime?: Date | null;
    nextAvailableMessage?: string;
  };
  rapido: {
    available: boolean;
    availableDates: Date[];
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  };
  standard: {
    available: boolean;
    earliestDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  };
}

export class UnifiedDeliveryCalculationService {
  /**
   * Transform partner schedule from object format to array format
   */
  static transformPartnerSchedule(
    schedule: PartnerScheduleObject | PartnerScheduleArray[]
  ): PartnerScheduleArray[] {
    // If already in array format, return as-is
    if (Array.isArray(schedule)) {
      console.log(
        "🔍 [SCHEDULE DEBUG] Already in array format, length:",
        schedule.length
      );
      return schedule;
    }

    console.log("🔍 [SCHEDULE DEBUG] Transforming object format schedule:", {
      hasMonday: "monday" in schedule,
      mondayValue: schedule.monday,
      mondayOpeningTime: schedule.mondayOpeningTime,
      mondayClosingTime: schedule.mondayClosingTime,
      scheduleKeys: Object.keys(schedule).slice(0, 10), // Show first 10 keys
    });

    // Transform object format to array format
    const dayMapping = [
      { day: "sunday", dayOfWeek: 0 },
      { day: "monday", dayOfWeek: 1 },
      { day: "tuesday", dayOfWeek: 2 },
      { day: "wednesday", dayOfWeek: 3 },
      { day: "thursday", dayOfWeek: 4 },
      { day: "friday", dayOfWeek: 5 },
      { day: "saturday", dayOfWeek: 6 },
    ];

    const result = dayMapping.map(({ day, dayOfWeek }) => ({
      dayOfWeek,
      openTime:
        (schedule[
          `${day}OpeningTime` as keyof PartnerScheduleObject
        ] as string) || "09:00",
      closeTime:
        (schedule[
          `${day}ClosingTime` as keyof PartnerScheduleObject
        ] as string) || "18:00",
      isOpen:
        (schedule[day as keyof PartnerScheduleObject] as boolean) || false,
    }));

    console.log("🔍 [SCHEDULE DEBUG] Transformed result:", result);
    return result;
  }

  /**
   * Main calculation method - implements exact logic from documentation
   */
  static async calculateDeliveryOptions(
    input: DeliveryInput,
    routeAvailability: DeliveryMethodAvailability
  ): Promise<DeliveryOptions> {
    const now = new Date();

    // Transform partner schedule to array format if needed
    const normalizedInput = {
      ...input,
      partnerSchedule: this.transformPartnerSchedule(input.partnerSchedule),
    };

    // Calculate based on expressDelivery flag
    if (normalizedInput.logistics.expressDelivery) {
      return this.calculateWhenExpressDeliveryEnabled(
        normalizedInput,
        routeAvailability,
        now
      );
    } else {
      return this.calculateWhenExpressDeliveryDisabled(
        normalizedInput,
        routeAvailability,
        now
      );
    }
  }

  /**
   * Synchronous version of calculateDeliveryOptions for batch processing optimization
   * Use this when you already have all the required data and don't need to make API calls
   */
  static calculateDeliveryOptionsSync(
    input: DeliveryInput,
    routeAvailability: DeliveryMethodAvailability
  ): DeliveryOptions {
    const now = new Date();

    // Transform partner schedule to array format if needed
    const normalizedInput = {
      ...input,
      partnerSchedule: this.transformPartnerSchedule(input.partnerSchedule),
    };

    // Calculate based on expressDelivery flag
    if (normalizedInput.logistics.expressDelivery) {
      return this.calculateWhenExpressDeliveryEnabledSync(
        normalizedInput,
        routeAvailability,
        now
      );
    } else {
      return this.calculateWhenExpressDeliveryDisabledSync(
        normalizedInput,
        routeAvailability,
        now
      );
    }
  }

  /**
   * Calculate delivery options when logistics.expressDelivery = true
   */
  private static async calculateWhenExpressDeliveryEnabled(
    input: NormalizedDeliveryInput,
    routeAvailability: DeliveryMethodAvailability,
    currentTime: Date
  ): Promise<DeliveryOptions> {
    // EXPRESS: Available if route supports it
    const expressResult = routeAvailability.express
      ? await this.calculateExpressDelivery(input, currentTime)
      : { available: false, reason: "No disponible en esta zona" };

    // RÁPIDO: Available if partner is open on next 2 calendar days
    const rapidoResult =
      routeAvailability.rapido && input.logistics.limitTime
        ? await this.calculateRapidoWhenExpressEnabled(input, currentTime)
        : {
            available: false,
            availableDates: [],
            reason: !routeAvailability.rapido
              ? "No disponible en esta zona"
              : "Este producto no admite entrega rápida",
          };

    // ESTÁNDAR: Always available, but must not overlap with Rápido dates
    const standardResult = routeAvailability.standard
      ? await this.calculateStandardWhenExpressEnabled(
          input,
          rapidoResult.availableDates,
          currentTime
        )
      : { available: false, reason: "No disponible en esta zona" };

    return {
      express: expressResult,
      rapido: rapidoResult,
      standard: standardResult,
    };
  }

  /**
   * Synchronous version: Calculate delivery options when logistics.expressDelivery = true
   */
  private static calculateWhenExpressDeliveryEnabledSync(
    input: NormalizedDeliveryInput,
    routeAvailability: DeliveryMethodAvailability,
    currentTime: Date
  ): DeliveryOptions {
    // EXPRESS: Available if route supports it
    const expressResult = routeAvailability.express
      ? this.calculateExpressDeliverySync(input, currentTime)
      : { available: false, reason: "No disponible en esta zona" };

    // RÁPIDO: Available if partner is open on next 2 calendar days
    const rapidoResult =
      routeAvailability.rapido && input.logistics.limitTime
        ? this.calculateRapidoWhenExpressEnabledSync(input, currentTime)
        : {
            available: false,
            availableDates: [],
            reason: !routeAvailability.rapido
              ? "No disponible en esta zona"
              : "Este producto no admite entrega rápida",
          };

    // ESTÁNDAR: Always available, but must not overlap with Rápido dates
    const standardResult = routeAvailability.standard
      ? this.calculateStandardWhenExpressEnabledSync(
          input,
          rapidoResult.availableDates,
          currentTime
        )
      : { available: false, reason: "No disponible en esta zona" };

    return {
      express: expressResult,
      rapido: rapidoResult,
      standard: standardResult,
    };
  }

  /**
   * Calculate delivery options when logistics.expressDelivery = false
   */
  private static async calculateWhenExpressDeliveryDisabled(
    input: NormalizedDeliveryInput,
    routeAvailability: DeliveryMethodAvailability,
    currentTime: Date
  ): Promise<DeliveryOptions> {
    // EXPRESS: Not available
    const expressResult = {
      available: false,
      reason: "Este producto no admite entrega express",
    };

    // RÁPIDO: Available if calculated date falls within next 2 calendar days
    const rapidoResult =
      routeAvailability.rapido && input.logistics.limitTime
        ? await this.calculateRapidoWhenExpressDisabled(input, currentTime)
        : {
            available: false,
            availableDates: [],
            reason: !routeAvailability.rapido
              ? "No disponible en esta zona"
              : "Este producto no admite entrega rápida",
          };

    // ESTÁNDAR: Available, but must start day after latest Rápido date
    const standardResult = routeAvailability.standard
      ? await this.calculateStandardWhenExpressDisabled(
          input,
          rapidoResult.availableDates,
          currentTime
        )
      : { available: false, reason: "No disponible en esta zona" };

    return {
      express: expressResult,
      rapido: rapidoResult,
      standard: standardResult,
    };
  }

  /**
   * Synchronous version: Calculate delivery options when logistics.expressDelivery = false
   */
  private static calculateWhenExpressDeliveryDisabledSync(
    input: NormalizedDeliveryInput,
    routeAvailability: DeliveryMethodAvailability,
    currentTime: Date
  ): DeliveryOptions {
    // EXPRESS: Not available
    const expressResult = {
      available: false,
      reason: "Este producto no admite entrega express",
    };

    // RÁPIDO: Available if calculated date falls within next 2 calendar days
    const rapidoResult =
      routeAvailability.rapido && input.logistics.limitTime
        ? this.calculateRapidoWhenExpressDisabledSync(input, currentTime)
        : {
            available: false,
            availableDates: [],
            reason: !routeAvailability.rapido
              ? "No disponible en esta zona"
              : "Este producto no admite entrega rápida",
          };

    // ESTÁNDAR: Available, but must start day after latest Rápido date
    const standardResult = routeAvailability.standard
      ? this.calculateStandardWhenExpressDisabledSync(
          input,
          rapidoResult.availableDates,
          currentTime
        )
      : { available: false, reason: "No disponible en esta zona" };

    return {
      express: expressResult,
      rapido: rapidoResult,
      standard: standardResult,
    };
  }

  /**
   * EXPRESS DELIVERY: Same day, within 3 hours
   * Available if current time is within partner working hours and before cutoff
   */
  private static async calculateExpressDelivery(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): Promise<{
    available: boolean;
    deliveryDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
    nextAvailableTime?: Date | null;
    nextAvailableMessage?: string;
  }> {
    console.log("🔍 [EXPRESS DEBUG] Starting express calculation:", {
      currentTime: currentTime.toISOString(),
      logistics: input.logistics,
      partnerScheduleLength: input.partnerSchedule.length,
    });

    // Get partner schedule for today
    const todaySchedule = this.getPartnerScheduleForDay(
      currentTime,
      input.partnerSchedule
    );

    console.log("🔍 [EXPRESS DEBUG] Today's schedule:", {
      dayOfWeek: currentTime.getDay(),
      todaySchedule,
    });

    if (!todaySchedule || !todaySchedule.isOpen) {
      return {
        available: false,
        reason: "Partner no disponible hoy para entrega express",
      };
    }

    // Parse times
    const limitTime = this.parseTimeString(input.logistics.limitTime);
    const partnerCloseTime = this.parseTimeString(todaySchedule.closeTime);
    const partnerOpenTime = this.parseTimeString(todaySchedule.openTime);

    console.log("🔍 [EXPRESS DEBUG] Parsed times:", {
      limitTimeInput: input.logistics.limitTime,
      limitTime,
      partnerOpenTime,
      partnerCloseTime,
    });

    if (!limitTime || !partnerCloseTime || !partnerOpenTime) {
      return { available: false, reason: "Error en configuración de horarios" };
    }

    // Calculate cutoff as earlier of limitTime or partner closeTime
    const limitDateTime = this.createTimeForToday(limitTime, currentTime);
    const partnerCloseDateTime = this.createTimeForToday(
      partnerCloseTime,
      currentTime
    );
    const partnerOpenDateTime = this.createTimeForToday(
      partnerOpenTime,
      currentTime
    );
    const cutoffTime =
      limitDateTime < partnerCloseDateTime
        ? limitDateTime
        : partnerCloseDateTime;

    console.log("🔍 [EXPRESS DEBUG] Calculated times:", {
      limitDateTime: limitDateTime.toISOString(),
      partnerCloseDateTime: partnerCloseDateTime.toISOString(),
      partnerOpenDateTime: partnerOpenDateTime.toISOString(),
      cutoffTime: cutoffTime.toISOString(),
    });

    // Check if current time is within working hours and before cutoff
    if (currentTime < partnerOpenDateTime) {
      return {
        available: false,
        reason: "Partner aún no está abierto",
        nextAvailableTime: partnerOpenDateTime,
        nextAvailableMessage: `Disponible a partir de las ${this.formatTime(
          partnerOpenDateTime
        )}`,
      };
    }

    if (currentTime >= cutoffTime) {
      // Check if partner will open again tomorrow
      const tomorrow = new Date(currentTime);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowSchedule = this.getPartnerScheduleForDay(
        tomorrow,
        input.partnerSchedule
      );

      let nextAvailableTime: Date | null = null;
      let nextAvailableMessage = "No disponible hoy";

      if (tomorrowSchedule && tomorrowSchedule.isOpen) {
        const tomorrowOpenTime = this.parseTimeString(
          tomorrowSchedule.openTime
        );
        if (tomorrowOpenTime) {
          nextAvailableTime = this.createTimeForDate(
            tomorrowOpenTime,
            tomorrow
          );
          nextAvailableMessage = `Disponible mañana a partir de las ${this.formatTime(
            nextAvailableTime
          )}`;
        }
      }

      return {
        available: false,
        reason: `Límite de hora para entrega express (${this.formatTime(
          cutoffTime
        )}) superado`,
        nextAvailableTime,
        nextAvailableMessage,
      };
    }

    // Calculate hours until cutoff
    const hoursUntilCutoff =
      (cutoffTime.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
    const hours = Math.floor(hoursUntilCutoff);
    const minutes = Math.floor((hoursUntilCutoff % 1) * 60);
    const countdown =
      hours > 0
        ? `${hours}h ${minutes}min restantes`
        : `${minutes}min restantes`;

    return {
      available: true,
      deliveryDate: new Date(
        currentTime.getFullYear(),
        currentTime.getMonth(),
        currentTime.getDate(),
        23,
        59,
        59
      ),
      cutoffTime,
      hoursUntilCutoff: Math.round(hoursUntilCutoff * 10) / 10, // Round to 1 decimal place
      countdown,
    };
  }

  /**
   * Synchronous version: EXPRESS DELIVERY calculation
   */
  private static calculateExpressDeliverySync(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): {
    available: boolean;
    deliveryDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
    nextAvailableTime?: Date | null;
    nextAvailableMessage?: string;
  } {
    console.log("🔍 [EXPRESS DEBUG] Starting express calculation:", {
      currentTime: currentTime.toISOString(),
      logistics: input.logistics,
      partnerScheduleLength: input.partnerSchedule.length,
    });

    // Get partner schedule for today
    const todaySchedule = this.getPartnerScheduleForDay(
      currentTime,
      input.partnerSchedule
    );

    console.log("🔍 [EXPRESS DEBUG] Today's schedule:", {
      dayOfWeek: currentTime.getDay(),
      todaySchedule,
    });

    if (!todaySchedule || !todaySchedule.isOpen) {
      return {
        available: false,
        reason: "Partner no disponible hoy para entrega express",
      };
    }

    // Parse times
    const limitTime = this.parseTimeString(input.logistics.limitTime);
    const partnerCloseTime = this.parseTimeString(todaySchedule.closeTime);
    const partnerOpenTime = this.parseTimeString(todaySchedule.openTime);

    console.log("🔍 [EXPRESS DEBUG] Parsed times:", {
      limitTimeInput: input.logistics.limitTime,
      limitTime,
      partnerOpenTime,
      partnerCloseTime,
    });

    if (!limitTime || !partnerCloseTime || !partnerOpenTime) {
      return { available: false, reason: "Error en configuración de horarios" };
    }

    // Calculate cutoff as earlier of limitTime or partner closeTime
    const limitDateTime = this.createTimeForToday(limitTime, currentTime);
    const partnerCloseDateTime = this.createTimeForToday(
      partnerCloseTime,
      currentTime
    );
    const partnerOpenDateTime = this.createTimeForToday(
      partnerOpenTime,
      currentTime
    );
    const cutoffTime =
      limitDateTime < partnerCloseDateTime
        ? limitDateTime
        : partnerCloseDateTime;

    console.log("🔍 [EXPRESS DEBUG] Calculated times:", {
      limitDateTime: limitDateTime.toISOString(),
      partnerCloseDateTime: partnerCloseDateTime.toISOString(),
      partnerOpenDateTime: partnerOpenDateTime.toISOString(),
      cutoffTime: cutoffTime.toISOString(),
    });

    // Check if current time is within working hours and before cutoff
    if (currentTime < partnerOpenDateTime) {
      return {
        available: false,
        reason: "Partner aún no está abierto",
        nextAvailableTime: partnerOpenDateTime,
        nextAvailableMessage: `Disponible a partir de las ${this.formatTime(
          partnerOpenDateTime
        )}`,
      };
    }

    if (currentTime >= cutoffTime) {
      // Check if partner will open again tomorrow
      const tomorrow = new Date(currentTime);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowSchedule = this.getPartnerScheduleForDay(
        tomorrow,
        input.partnerSchedule
      );

      let nextAvailableTime: Date | null = null;
      let nextAvailableMessage = "No disponible hoy";

      if (tomorrowSchedule && tomorrowSchedule.isOpen) {
        const tomorrowOpenTime = this.parseTimeString(
          tomorrowSchedule.openTime
        );
        if (tomorrowOpenTime) {
          nextAvailableTime = this.createTimeForDate(
            tomorrowOpenTime,
            tomorrow
          );
          nextAvailableMessage = `Disponible mañana a partir de las ${this.formatTime(
            nextAvailableTime
          )}`;
        }
      }

      return {
        available: false,
        reason: `Límite de hora para entrega express (${this.formatTime(
          cutoffTime
        )}) superado`,
        nextAvailableTime,
        nextAvailableMessage,
      };
    }

    // Calculate hours until cutoff
    const hoursUntilCutoff =
      (cutoffTime.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
    const hours = Math.floor(hoursUntilCutoff);
    const minutes = Math.floor((hoursUntilCutoff % 1) * 60);
    const countdown =
      hours > 0
        ? `${hours}h ${minutes}min restantes`
        : `${minutes}min restantes`;

    return {
      available: true,
      deliveryDate: new Date(
        currentTime.getFullYear(),
        currentTime.getMonth(),
        currentTime.getDate(),
        23,
        59,
        59
      ),
      cutoffTime,
      hoursUntilCutoff: Math.round(hoursUntilCutoff * 10) / 10, // Round to 1 decimal place
      countdown,
    };
  }

  /**
   * RÁPIDO DELIVERY when expressDelivery = true
   * Available if partner is open on any of the next 2 calendar days
   */
  private static async calculateRapidoWhenExpressEnabled(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): Promise<{
    available: boolean;
    availableDates: Date[];
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  }> {
    const availableDates: Date[] = [];

    // Check next 2 calendar days
    for (let i = 1; i <= 2; i++) {
      const candidateDate = new Date(currentTime);
      candidateDate.setDate(candidateDate.getDate() + i);

      const daySchedule = this.getPartnerScheduleForDay(
        candidateDate,
        input.partnerSchedule
      );
      if (daySchedule && daySchedule.isOpen) {
        // Set to end of day for delivery
        const deliveryDate = new Date(
          candidateDate.getFullYear(),
          candidateDate.getMonth(),
          candidateDate.getDate(),
          23,
          59,
          59
        );
        availableDates.push(deliveryDate);
      }
    }

    if (availableDates.length === 0) {
      return {
        available: false,
        availableDates: [],
        countdown: null,
        reason: "Partner no disponible en los próximos 2 días",
      };
    }

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown =
      availableDates.length > 0
        ? this.formatDeliveryCountdown(availableDates[0], countdownInfo)
        : countdownInfo.countdown;

    return {
      available: true,
      availableDates,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * Synchronous version: RÁPIDO DELIVERY when expressDelivery = true
   */
  private static calculateRapidoWhenExpressEnabledSync(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): {
    available: boolean;
    availableDates: Date[];
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  } {
    const availableDates: Date[] = [];

    // Check next 2 calendar days
    for (let i = 1; i <= 2; i++) {
      const candidateDate = new Date(currentTime);
      candidateDate.setDate(candidateDate.getDate() + i);

      const daySchedule = this.getPartnerScheduleForDay(
        candidateDate,
        input.partnerSchedule
      );
      if (daySchedule && daySchedule.isOpen) {
        // Set to end of day for delivery
        const deliveryDate = new Date(
          candidateDate.getFullYear(),
          candidateDate.getMonth(),
          candidateDate.getDate(),
          23,
          59,
          59
        );
        availableDates.push(deliveryDate);
      }
    }

    if (availableDates.length === 0) {
      return {
        available: false,
        availableDates: [],
        countdown: null,
        reason: "Partner no disponible en los próximos 2 días",
      };
    }

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown =
      availableDates.length > 0
        ? this.formatDeliveryCountdown(availableDates[0], countdownInfo)
        : countdownInfo.countdown;

    return {
      available: true,
      availableDates,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * RÁPIDO DELIVERY when expressDelivery = false
   * Available if calculated date falls within next 2 calendar days
   */
  private static async calculateRapidoWhenExpressDisabled(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): Promise<{
    available: boolean;
    availableDates: Date[];
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  }> {
    const receivedDate = this.calculateReceivedDate(
      currentTime,
      input.partnerSchedule
    );
    const daysToDeliver = input.logistics.daysToDeliver;

    // Calculate delivery date: receivedDate + daysToDeliver (business days) + 1 business day + opsLeadTime
    let deliveryDate = new Date(receivedDate);

    // Add daysToDeliver business days
    deliveryDate = this.addBusinessDays(
      deliveryDate,
      daysToDeliver,
      input.partnerSchedule
    );

    // Add 1 business day (readyForPickupDate)
    deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);

    // Add opsLeadTime
    deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);

    // Check if delivery date falls within next 2 calendar days
    const maxDate = new Date(currentTime);
    maxDate.setDate(maxDate.getDate() + 2);

    if (deliveryDate <= maxDate) {
      const finalDeliveryDate = new Date(
        deliveryDate.getFullYear(),
        deliveryDate.getMonth(),
        deliveryDate.getDate(),
        23,
        59,
        59
      );

      // Calculate countdown for ordering cutoff
      const countdownInfo = this.calculateCountdown(input, currentTime);
      const formattedCountdown = this.formatDeliveryCountdown(
        finalDeliveryDate,
        countdownInfo
      );

      return {
        available: true,
        availableDates: [finalDeliveryDate],
        cutoffTime: countdownInfo.cutoffTime,
        hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
        countdown: formattedCountdown,
      };
    }

    return {
      available: false,
      availableDates: [],
      countdown: null,
      reason: "Fecha calculada excede ventana de entrega rápida",
    };
  }

  /**
   * Synchronous version: RÁPIDO DELIVERY when expressDelivery = false
   */
  private static calculateRapidoWhenExpressDisabledSync(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): {
    available: boolean;
    availableDates: Date[];
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  } {
    const receivedDate = this.calculateReceivedDate(
      currentTime,
      input.partnerSchedule
    );
    const daysToDeliver = input.logistics.daysToDeliver;

    // Calculate delivery date: receivedDate + daysToDeliver (business days) + 1 business day + opsLeadTime
    let deliveryDate = new Date(receivedDate);

    // Add daysToDeliver business days
    deliveryDate = this.addBusinessDays(
      deliveryDate,
      daysToDeliver,
      input.partnerSchedule
    );

    // Add 1 business day (readyForPickupDate)
    deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);

    // Add opsLeadTime
    deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);

    // Check if delivery date falls within next 2 calendar days
    const maxDate = new Date(currentTime);
    maxDate.setDate(maxDate.getDate() + 2);

    if (deliveryDate <= maxDate) {
      const finalDeliveryDate = new Date(
        deliveryDate.getFullYear(),
        deliveryDate.getMonth(),
        deliveryDate.getDate(),
        23,
        59,
        59
      );

      // Calculate countdown for ordering cutoff
      const countdownInfo = this.calculateCountdown(input, currentTime);
      const formattedCountdown = this.formatDeliveryCountdown(
        finalDeliveryDate,
        countdownInfo
      );

      return {
        available: true,
        availableDates: [finalDeliveryDate],
        cutoffTime: countdownInfo.cutoffTime,
        hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
        countdown: formattedCountdown,
      };
    }

    return {
      available: false,
      availableDates: [],
      countdown: null,
      reason: "Fecha calculada excede ventana de entrega rápida",
    };
  }

  /**
   * ESTÁNDAR DELIVERY when expressDelivery = true
   * Must not overlap with Rápido dates, daysToDeliver forced to 0
   */
  private static async calculateStandardWhenExpressEnabled(
    input: NormalizedDeliveryInput,
    rapidoAvailableDates: Date[],
    currentTime: Date
  ): Promise<{
    available: boolean;
    earliestDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  }> {
    const receivedDate = this.calculateReceivedDate(
      currentTime,
      input.partnerSchedule
    );

    // daysToDeliver forced to 0 when expressDelivery = true
    let deliveryDate = new Date(receivedDate);

    // Add 1 business day (readyForPickupDate)
    deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);

    // Add opsLeadTime
    deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);

    // Ensure it doesn't overlap with Rápido dates
    if (rapidoAvailableDates.length > 0) {
      const latestRapidoDate =
        rapidoAvailableDates[rapidoAvailableDates.length - 1];
      if (deliveryDate <= latestRapidoDate) {
        deliveryDate = new Date(latestRapidoDate);
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }
    }

    const finalDeliveryDate = new Date(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate(),
      12,
      0,
      0
    ); // Noon

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown = this.formatDeliveryCountdown(
      finalDeliveryDate,
      countdownInfo
    );

    return {
      available: true,
      earliestDate: finalDeliveryDate,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * Synchronous version: ESTÁNDAR DELIVERY when expressDelivery = true
   */
  private static calculateStandardWhenExpressEnabledSync(
    input: NormalizedDeliveryInput,
    rapidoAvailableDates: Date[],
    currentTime: Date
  ): {
    available: boolean;
    earliestDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  } {
    const receivedDate = this.calculateReceivedDate(
      currentTime,
      input.partnerSchedule
    );

    // daysToDeliver forced to 0 when expressDelivery = true
    let deliveryDate = new Date(receivedDate);

    // Add 1 business day (readyForPickupDate)
    deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);

    // Add opsLeadTime
    deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);

    // Ensure it doesn't overlap with Rápido dates
    if (rapidoAvailableDates.length > 0) {
      const latestRapidoDate =
        rapidoAvailableDates[rapidoAvailableDates.length - 1];
      if (deliveryDate <= latestRapidoDate) {
        deliveryDate = new Date(latestRapidoDate);
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }
    }

    const finalDeliveryDate = new Date(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate(),
      12,
      0,
      0
    ); // Noon

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown = this.formatDeliveryCountdown(
      finalDeliveryDate,
      countdownInfo
    );

    return {
      available: true,
      earliestDate: finalDeliveryDate,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * ESTÁNDAR DELIVERY when expressDelivery = false
   * Must start day after latest Rápido date
   */
  private static async calculateStandardWhenExpressDisabled(
    input: NormalizedDeliveryInput,
    rapidoAvailableDates: Date[],
    currentTime: Date
  ): Promise<{
    available: boolean;
    earliestDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  }> {
    let deliveryDate: Date;

    if (rapidoAvailableDates.length > 0) {
      // Must start day after latest Rápido date
      const latestRapidoDate =
        rapidoAvailableDates[rapidoAvailableDates.length - 1];
      deliveryDate = new Date(latestRapidoDate);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      // Same calculation as Rápido but offset forward
      const receivedDate = this.calculateReceivedDate(
        currentTime,
        input.partnerSchedule
      );
      const daysToDeliver = input.logistics.daysToDeliver;

      deliveryDate = new Date(receivedDate);
      deliveryDate = this.addBusinessDays(
        deliveryDate,
        daysToDeliver,
        input.partnerSchedule
      );
      // REMOVED: Extra +1 business day step (was incorrect)
      // deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);

      deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);
    }

    const finalDeliveryDate = new Date(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate(),
      12,
      0,
      0
    ); // Noon

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown = this.formatDeliveryCountdown(
      finalDeliveryDate,
      countdownInfo
    );

    return {
      available: true,
      earliestDate: finalDeliveryDate,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * Synchronous version: ESTÁNDAR DELIVERY when expressDelivery = false
   */
  private static calculateStandardWhenExpressDisabledSync(
    input: NormalizedDeliveryInput,
    rapidoAvailableDates: Date[],
    currentTime: Date
  ): {
    available: boolean;
    earliestDate?: Date;
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
    reason?: string;
  } {
    let deliveryDate: Date;

    if (rapidoAvailableDates.length > 0) {
      // Must start day after latest Rápido date
      const latestRapidoDate =
        rapidoAvailableDates[rapidoAvailableDates.length - 1];
      deliveryDate = new Date(latestRapidoDate);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      // Same calculation as Rápido but offset forward
      const receivedDate = this.calculateReceivedDate(
        currentTime,
        input.partnerSchedule
      );
      const daysToDeliver = input.logistics.daysToDeliver;

      deliveryDate = new Date(receivedDate);
      deliveryDate = this.addBusinessDays(
        deliveryDate,
        daysToDeliver,
        input.partnerSchedule
      );
      // REMOVED: Extra +1 business day step (was incorrect)
      // deliveryDate = this.addBusinessDays(deliveryDate, 1, input.partnerSchedule);
      deliveryDate = this.addCalendarDays(deliveryDate, input.opsLeadTime);
    }

    const finalDeliveryDate = new Date(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate(),
      12,
      0,
      0
    ); // Noon

    // Calculate countdown for ordering cutoff
    const countdownInfo = this.calculateCountdown(input, currentTime);
    const formattedCountdown = this.formatDeliveryCountdown(
      finalDeliveryDate,
      countdownInfo
    );

    return {
      available: true,
      earliestDate: finalDeliveryDate,
      cutoffTime: countdownInfo.cutoffTime,
      hoursUntilCutoff: countdownInfo.hoursUntilCutoff,
      countdown: formattedCountdown,
    };
  }

  /**
   * Calculate received date based on partner hours
   */
  private static calculateReceivedDate(
    currentTime: Date,
    partnerSchedule: PartnerScheduleArray[]
  ): Date {
    const todaySchedule = this.getPartnerScheduleForDay(
      currentTime,
      partnerSchedule
    );

    if (!todaySchedule || !todaySchedule.isOpen) {
      // Partner not open today, order received on next open day
      return this.getNextOpenDay(currentTime, partnerSchedule);
    }

    const openTime = this.parseTimeString(todaySchedule.openTime);
    const closeTime = this.parseTimeString(todaySchedule.closeTime);

    if (!openTime || !closeTime) {
      return new Date(currentTime); // Fallback to today
    }

    const openDateTime = this.createTimeForToday(openTime, currentTime);
    const closeDateTime = this.createTimeForToday(closeTime, currentTime);

    // If placed during opening hours → today
    // If placed before opening hours → today
    // If placed after closing hours → next open day
    if (currentTime >= openDateTime && currentTime <= closeDateTime) {
      // During opening hours - received today
      return new Date(
        currentTime.getFullYear(),
        currentTime.getMonth(),
        currentTime.getDate()
      );
    } else if (currentTime < openDateTime) {
      // Before opening hours - still received today
      return new Date(
        currentTime.getFullYear(),
        currentTime.getMonth(),
        currentTime.getDate()
      );
    } else {
      // After closing hours - next open day
      return this.getNextOpenDay(currentTime, partnerSchedule);
    }
  }

  /**
   * Helper methods
   */
  private static getPartnerScheduleForDay(
    date: Date,
    schedule: PartnerScheduleArray[]
  ) {
    const dayOfWeek = date.getDay();
    return schedule.find((s) => s.dayOfWeek === dayOfWeek);
  }

  /**
   * Calculate countdown for delivery methods based on cutoff time
   * When partners are closed, sets cutoff to midnight
   */
  private static calculateCountdown(
    input: NormalizedDeliveryInput,
    currentTime: Date
  ): {
    cutoffTime?: Date;
    hoursUntilCutoff?: number;
    countdown?: string | null;
  } {
    // Parse the ordering cutoff time (limitTime)
    const limitTime = this.parseTimeString(input.logistics.limitTime);

    if (!limitTime) {
      return { countdown: null };
    }

    let cutoffTime: Date;

    // Check if partner is currently open
    const isPartnerOpen = this.isPartnerCurrentlyOpen(
      currentTime,
      input.partnerSchedule
    );

    if (!isPartnerOpen) {
      // Partner is closed - set cutoff to midnight (end of current day)
      cutoffTime = new Date(currentTime);
      cutoffTime.setHours(23, 59, 59, 999); // End of today
    } else {
      // Partner is open - use normal limitTime logic
      cutoffTime = this.createTimeForToday(limitTime, currentTime);

      // If we've passed today's cutoff, calculate for tomorrow
      if (currentTime >= cutoffTime) {
        cutoffTime = new Date(cutoffTime.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
      }
    }

    // Calculate hours until cutoff
    const hoursUntilCutoff =
      (cutoffTime.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
    const hours = Math.floor(hoursUntilCutoff);
    const minutes = Math.floor((hoursUntilCutoff % 1) * 60);

    let countdown: string;
    if (hours > 24) {
      // More than 24 hours - show days and hours
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      countdown = `${days}d ${remainingHours}h restantes`;
    } else if (hours > 0) {
      countdown = `${hours}h ${minutes}min restantes`;
    } else {
      countdown = `${minutes}min restantes`;
    }

    return {
      cutoffTime,
      hoursUntilCutoff: Math.round(hoursUntilCutoff * 10) / 10,
      countdown,
    };
  }

  /**
   * Format countdown for delivery methods with delivery date
   * Format: "A partir del jue 12 de jun si lo pides dentro de las 0h 10min"
   */
  private static formatDeliveryCountdown(
    deliveryDate: Date,
    countdownInfo: { countdown?: string | null }
  ): string | null {
    if (!countdownInfo.countdown || !deliveryDate) {
      return null;
    }

    // Format delivery date as "jue 12 de jun" (abbreviated weekday and month, no year)
    const deliveryDateStr = deliveryDate
      .toLocaleDateString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
      .replace(".", ""); // Remove period after abbreviated day

    return `A partir del ${deliveryDateStr} si lo pides dentro de las ${countdownInfo.countdown}`;
  }

  /**
   * Check if partner is currently open based on schedule and current time
   */
  private static isPartnerCurrentlyOpen(
    currentTime: Date,
    partnerSchedule: PartnerScheduleArray[]
  ): boolean {
    const todaySchedule = this.getPartnerScheduleForDay(
      currentTime,
      partnerSchedule
    );

    if (!todaySchedule || !todaySchedule.isOpen) {
      return false;
    }

    const openTime = this.parseTimeString(todaySchedule.openTime);
    const closeTime = this.parseTimeString(todaySchedule.closeTime);

    if (!openTime || !closeTime) {
      return false;
    }

    const openDateTime = this.createTimeForToday(openTime, currentTime);
    const closeDateTime = this.createTimeForToday(closeTime, currentTime);

    return currentTime >= openDateTime && currentTime <= closeDateTime;
  }

  private static parseTimeString(
    timeStr: string | boolean
  ): { hours: number; minutes: number } | null {
    if (typeof timeStr === "boolean") {
      return timeStr ? { hours: 14, minutes: 0 } : null; // Default to 14:00 if true
    }

    // Handle both HH:MM and HH:MM:SS formats
    const timeParts = timeStr.split(":").map(Number);
    const hours = timeParts[0];
    const minutes = timeParts[1] || 0; // Default to 0 if minutes not provided

    if (isNaN(hours) || isNaN(minutes)) return null;
    return { hours, minutes };
  }

  private static createTimeForToday(
    time: { hours: number; minutes: number },
    baseDate: Date
  ): Date {
    const result = new Date(baseDate);
    result.setHours(time.hours, time.minutes, 0, 0);
    return result;
  }

  private static createTimeForDate(
    time: { hours: number; minutes: number },
    targetDate: Date
  ): Date {
    const result = new Date(targetDate);
    result.setHours(time.hours, time.minutes, 0, 0);
    return result;
  }

  private static formatTime(date: Date): string {
    return date.toLocaleTimeString("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  private static addBusinessDays(
    startDate: Date,
    businessDays: number,
    partnerSchedule: PartnerScheduleArray[]
  ): Date {
    let result = new Date(startDate);
    let addedDays = 0;

    while (addedDays < businessDays) {
      result.setDate(result.getDate() + 1);
      const daySchedule = this.getPartnerScheduleForDay(
        result,
        partnerSchedule
      );

      if (daySchedule && daySchedule.isOpen) {
        addedDays++;
      }
    }

    return result;
  }

  private static addCalendarDays(startDate: Date, days: number): Date {
    const result = new Date(startDate);
    result.setDate(result.getDate() + days);
    return result;
  }

  private static getNextOpenDay(
    startDate: Date,
    partnerSchedule: PartnerScheduleArray[]
  ): Date {
    let result = new Date(startDate);
    result.setDate(result.getDate() + 1); // Start checking from tomorrow

    let iterations = 0;
    while (iterations < 14) {
      // Prevent infinite loop
      const daySchedule = this.getPartnerScheduleForDay(
        result,
        partnerSchedule
      );
      if (daySchedule && daySchedule.isOpen) {
        return new Date(
          result.getFullYear(),
          result.getMonth(),
          result.getDate()
        );
      }
      result.setDate(result.getDate() + 1);
      iterations++;
    }

    // Fallback to tomorrow if no open day found in 2 weeks
    return new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + 1
    );
  }

  /**
   * Format delivery date for display
   */
  static formatDeliveryDateForDisplay(date: Date): string {
    return DateService.formatDeliveryDateForDisplay(date);
  }
}
